import { motion } from "framer-motion";
import { Download } from "lucide-react";
import React, { useState } from "react";
import { AppCard, KpiCard, KpiIcons, Money, ProgressBar, SectionTitle } from "../components/ui/AppCard";
import { MonthlyCashFlowChart } from "../components/MonthlyCashFlowChart";
import { useCurrency } from "../hooks/useCurrency";
import { useBudgetSummary, useMonthlyBreakdown } from "../hooks/useBudgetData";
import { monthShortLabel } from "../i18n/dates";
import { useT } from "../i18n/useT";
import { exportAnnualReportFromDb } from "../lib/pdf/exportFromDb";
import { leftoverTone, savingsRateTone, SEMANTIC } from "../lib/semanticColors";

interface Props { year: number; person: string }

export function DashboardPage({ year, person }: Props) {
  const { format, currency, locale } = useCurrency();
  const { t, locale: uiLocale } = useT();
  const summary = useBudgetSummary(year, person);
  const monthly = useMonthlyBreakdown(year, person);
  const [exporting, setExporting] = useState(false);

  const savingsPct = summary.totalSavedTarget > 0
    ? (summary.actualSaved / summary.totalSavedTarget) * 100
    : 0;

  const rateTone = savingsRateTone(summary.savingsRate);
  const leftTone = leftoverTone(summary.leftover);
  const savingsRateColor = SEMANTIC[rateTone].bar;

  const handleAnnualPdf = async () => {
    setExporting(true);
    try {
      await exportAnnualReportFromDb({
        year,
        person,
        currency,
        locale,
        uiLocale,
        formatCompact: (n) => format(n, true),
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="p-4 lg:p-6 space-y-6 w-full max-w-full overflow-x-hidden">

      {/* ── KPI row ───────────────────────────────────── */}
      <div>
        <SectionTitle
          action={
            <button
              type="button"
              className="app-btn-outline text-xs no-print min-h-[40px]"
              disabled={exporting}
              onClick={() => void handleAnnualPdf()}
            >
              <Download size={13} /> {exporting ? t("dash.buildingPdf") : t("dash.downloadReport")}
            </button>
          }
        >
          {t("dash.yearGlance", { year })}
        </SectionTitle>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {[
            {
              label: t("dash.kpi.moneyIn"),
              value: format(summary.income, true),
              color: SEMANTIC.neutral.kpi,
              icon: <KpiIcons.moneyIn size={16} />,
              index: 0,
            },
            {
              label: t("dash.kpi.moneyOut"),
              value: format(summary.expense, true),
              color: SEMANTIC.neutral.kpi,
              icon: <KpiIcons.moneyOut size={16} />,
              index: 1,
            },
            {
              label: t("dash.kpi.leftover"),
              value: format(summary.leftover, true),
              color: SEMANTIC[leftTone].kpi,
              icon: <KpiIcons.leftover size={16} />,
              index: 2,
            },
            {
              label: t("dash.kpi.savingsRate"),
              value: `${summary.savingsRate.toFixed(1)}%`,
              color: SEMANTIC[rateTone].kpi,
              icon: <KpiIcons.rate size={16} />,
              index: 3,
            },
            {
              label: t("dash.kpi.totalSaved"),
              value: format(summary.actualSaved, true),
              color: SEMANTIC.positive.kpi,
              icon: <KpiIcons.savings size={16} />,
              index: 4,
            },
          ].map((kpi) => (
            <KpiCard
              key={kpi.label}
              label={kpi.label}
              value={kpi.value}
              color={kpi.color}
              icon={kpi.icon}
              index={kpi.index}
            />
          ))}
        </div>
      </div>

      {/* ── Elevated Monthly Cash Flow Chart Component ── */}
      <div>
        <MonthlyCashFlowChart monthly={monthly} year={year} />
      </div>

      {/* ── Bottom row ────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

        {/* Savings Goal */}
        <AppCard padding="md">
          <SectionTitle>{t("dash.savingsGoal")}</SectionTitle>
          <div className="text-center py-4">
            <p className="text-4xl tracking-tight" style={{ color: savingsRateColor, fontFamily: "var(--font-display, inherit)" }}>
              {summary.savingsRate.toFixed(1)}%
            </p>
            <p className="text-xs text-muted-foreground mt-1">{t("dash.savingsRateTarget")}</p>
          </div>
          <div className="space-y-3">
            <ProgressBar
              value={savingsPct}
              label={`${format(summary.actualSaved)} / ${format(summary.totalSavedTarget)}`}
              color={SEMANTIC.positive.bar}
            />
            <ProgressBar
              value={summary.savingsRate}
              label={t("dash.rateVsTarget")}
              color={savingsRateColor}
            />
          </div>
        </AppCard>

        {/* Month highlights */}
        <AppCard padding="md">
          <SectionTitle>{t("dash.monthHighlights")}</SectionTitle>
          <div className="space-y-2.5 mt-1">
            {monthly
              .filter((m) => m.totalIncome > 0)
              .sort((a, b) => b.savingsRate - a.savingsRate)
              .slice(0, 6)
              .map((m) => {
                const label = monthShortLabel(m.month, uiLocale, year);
                const pct   = Math.min(100, Math.round(m.savingsRate));
                const color = pct >= 20 ? "#22c55e" : pct >= 10 ? "#f59e0b" : "#ef4444";
                return (
                  <div key={m.month} className="flex items-center gap-2">
                    <span className="w-8 text-[10px] font-semibold text-muted-foreground">{label}</span>
                    <div className="flex-1 h-3 rounded-full overflow-hidden bg-muted relative">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${pct}%` }}
                        transition={{ duration: 0.5, delay: 0.05 }}
                        className="h-full rounded-full"
                        style={{ backgroundColor: color }}
                      />
                    </div>
                    <span className="w-9 text-right text-[11px] font-semibold">{pct}%</span>
                    <Money amount={m.leftover} className="text-[11px] w-20 text-right" colored />
                  </div>
                );
              })}
          </div>
        </AppCard>
      </div>
    </div>
  );
}
